/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import AppearanceControlsButton from '../appearance-controls/button';
import type {
	ColorAndComponents,
	ColorMode,
	Iconography,
	SectionIds,
	Typography,
	VisualRefreshMode,
} from '../types';

import CodeSandbox, { type CodeSandboxProps } from './code-sandbox';
import Copy from './copy';

const styles = cssMap({
	root: {
		display: 'grid',
		backgroundColor: token('elevation.surface.sunken'),
	},
	rootShowcaseVisible: {
		gridTemplateColumns: 'min-content auto',
	},
	rootShowcaseHidden: {
		gridTemplateColumns: 'auto',
		borderTopLeftRadius: token('border.radius'),
		borderTopRightRadius: token('border.radius'),
	},
	rootSourceCodeVisible: {
		borderBottomColor: token('color.border'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
	},
	rootSourceCodeHidden: {
		borderBottomLeftRadius: token('border.radius'),
		borderBottomRightRadius: token('border.radius'),
	},
	section: {
		paddingBlock: token('space.050'),
		paddingInline: token('space.150'),
	},
	appearanceControls: {
		borderRightColor: token('color.border'),
		borderRightWidth: token('border.width'),
		borderRightStyle: 'solid',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
	},
	buttonGroup: {
		display: 'flex',
		flexWrap: 'wrap',
		justifyContent: 'flex-end',
		gap: token('space.050'),
	},
});

type ExampleActionsProps = {
	isShowcaseVisible: boolean;
	isSourceCodeVisible: boolean;
	isCodeSandboxDisabled?: boolean;
	codeSandboxInfo?: CodeSandboxProps;
	sourceCode: string;
	appearanceControls: {
		colorAndComponents?: ColorAndComponents;
		colorMode?: ColorMode;
		iconography?: Iconography;
		onColorAndComponentsChange?(colorAndComponents: ColorAndComponents): void;
		onColorModeChange?(colorMode: ColorMode): void;
		onIconographyChange?(typography: Iconography): void;
		onTypographyChange?(typography: Typography): void;
		onVisualRefreshModeChange?(visualRefreshMode: VisualRefreshMode): void;
		typography?: Typography;
		visualRefreshMode?: VisualRefreshMode;
		hiddenSections?: SectionIds;
	};
	testId?: string;
};

/**
 * __Example actions__.
 *
 * Example actions sit within an example displays various actions for the user.
 *
 */
export const ExampleActions = ({
	isShowcaseVisible,
	isSourceCodeVisible,
	isCodeSandboxDisabled,
	codeSandboxInfo,
	sourceCode,
	appearanceControls,
	testId,
}: ExampleActionsProps) => {
	return (
		<div
			css={[
				styles.root,
				isShowcaseVisible ? styles.rootShowcaseVisible : styles.rootShowcaseHidden,
				isSourceCodeVisible ? styles.rootSourceCodeVisible : styles.rootSourceCodeHidden,
			]}
		>
			{isShowcaseVisible && (
				<div css={[styles.appearanceControls, styles.section]}>
					<AppearanceControlsButton
						colorAndComponents={appearanceControls.colorAndComponents}
						colorMode={appearanceControls.colorMode}
						iconography={appearanceControls.iconography}
						onColorAndComponentsChange={appearanceControls.onColorAndComponentsChange}
						onColorModeChange={appearanceControls.onColorModeChange}
						onIconographyChange={appearanceControls.onIconographyChange}
						onTypographyChange={appearanceControls.onTypographyChange}
						onVisualRefreshModeChange={appearanceControls.onVisualRefreshModeChange}
						popupPlacement="bottom-start"
						typography={appearanceControls.typography}
						visualRefreshMode={appearanceControls.visualRefreshMode}
						testId={testId ? `${testId}--appearance-controls` : undefined}
						hiddenSections={appearanceControls.hiddenSections}
					/>
				</div>
			)}
			<div css={[styles.section, styles.buttonGroup]}>
				<Copy source={sourceCode} />
				{!isCodeSandboxDisabled && codeSandboxInfo && <CodeSandbox {...codeSandboxInfo} />}
			</div>
		</div>
	);
};
